import { useState } from 'react';
// Types
import { EventTicketTierDraft } from 'helpers/ticketEvents';
// Ui
import Text from 'ui3/Text/Text';
import Button from 'ui3/Button/Button';
// Components
import TicketTierRow from '../TicketTierRow/TicketTierRow';
import AddTierModal from '../AddTierModal/AddTierModal';
// Styles
import styles from './TicketTiersEditor.module.scss';

type TicketTiersEditorProps = {
  tiers: EventTicketTierDraft[];
  onChange: (tiers: EventTicketTierDraft[]) => void;
  error?: string;
  canAddTiers?: boolean;
  /** Heading shown while no tier is added yet. */
  emptyLabel?: string;
};

/** The tier list of an event's tickets; a tier the event already sells can't be removed. */
const TicketTiersEditor = ({
  tiers,
  onChange,
  error,
  canAddTiers = true,
  emptyLabel,
}: TicketTiersEditorProps): JSX.Element => {
  const [isAddTierOpen, setIsAddTierOpen] = useState(false);

  return (
    <>
      <div className={styles.root}>
        {tiers.length > 0 ? (
          <Text variant="body2Medium" color="lights-low">
            TICKET TIERS
          </Text>
        ) : (
          emptyLabel && <Text variant="body2Medium">{emptyLabel}</Text>
        )}
        {tiers.map((tier) => (
          <TicketTierRow
            key={tier.id}
            tier={tier}
            onRemove={
              tier.isSaved
                ? undefined
                : () => onChange(tiers.filter(({ id }) => id !== tier.id))
            }
          />
        ))}
        {canAddTiers && (
          <Button
            type="button"
            variant="secondary"
            size="medium"
            icon="plus"
            className={styles.addTierButton}
            onClick={() => setIsAddTierOpen(true)}
          >
            Add Tier
          </Button>
        )}
        {error && (
          <Text variant="captionRegular" color="error-default">
            {error}
          </Text>
        )}
      </div>

      <AddTierModal
        open={isAddTierOpen}
        onClose={() => setIsAddTierOpen(false)}
        onAdd={(tier) => onChange([...tiers, tier])}
      />
    </>
  );
};

export default TicketTiersEditor;
